import Controller from "@ember/controller";
import { action, computed } from "@ember/object";
import { service } from "@ember/service";
import { i18n } from "discourse-i18n";

export default class CircleController extends Controller {
  @service currentUser;
  @service siteSettings;

  destroying = null;
  showTooltip = false;

  @computed(
    "showMessages",
    "showMembers",
    "showStaff",
    "showRequests",
    "showManage",
    "model.member_group.user_count",
    "model.member_group.request_count",
    "model.staff_group.user_count"
  )
  get availableTabs() {
    const userCount = this.model.member_group.user_count;
    const requestCount = this.model.member_group.request_count;
    const staffCount = this.model.staff_group.user_count;

    const defaultTabs = [
      new Tab({
        name: "about",
        i18nKey: "discourse_circles.circle.about.title",
      }),
    ];

    if (this.showActivity) {
      defaultTabs.push(
        new Tab({
          name: "activity",
          i18nKey: "discourse_circles.circle.activity.title",
        })
      );
    }

    if (this.showMessages) {
      defaultTabs.push(
        new Tab({
          name: "messages",
        })
      );
    }

    if (this.showMembers) {
      defaultTabs.push(
        new Tab({
          name: "members",
          i18nKey: "discourse_circles.circle.members.title",
          icon: "users",
          options: {
            count: userCount,
          },
        })
      );
    }

    if (this.showRequests) {
      defaultTabs.push(
        new Tab({
          name: "requests",
          i18nKey: "discourse_circles.circle.requests.title",
          icon: "user-plus",
          options: {
            showNotification: requestCount > 0,
            count: requestCount,
          },
        })
      );
    }

    if (this.showStaff) {
      defaultTabs.push(
        new Tab({
          name: "staff",
          i18nKey: "discourse_circles.circle.staff.title",
          icon: "users",
          options: {
            count: staffCount,
          },
        })
      );
    }

    if (this.showManage) {
      defaultTabs.push(
        new Tab({
          name: "manage",
          i18nKey: "discourse_circles.circle.manage.title",
          icon: "wrench",
        })
      );
    }

    return defaultTabs;
  }

  @computed("availableTabs")
  get displayedTabs() {
    return this.availableTabs;
  }

  get showActivity() {
    return (
      this.model.can_read_topics ||
      this.currentUser ||
      this.siteSettings
        .discourse_circles_allowed_users_can_see_public_circles_activity
    );
  }

  @computed("model.is_circle_owner")
  get showMembershipButton() {
    return !this.model.is_circle_owner;
  }

  @computed(
    "model.member_group.has_messages",
    "model.member_group.is_group_user",
    "model.staff_group.has_messages",
    "model.staff_group.is_group_user"
  )
  get showMessages() {
    const hasMembersMessages = this.model.member_group.has_messages;
    const isMember = this.model.member_group.is_group_user;
    const hasStaffMessages = this.model.staff_group.has_messages;
    const isStaff = this.model.staff_group.is_group_user;

    if (!this.currentUser?.can_send_private_messages) {
      return false;
    }

    if (!hasMembersMessages && !hasStaffMessages) {
      return false;
    }

    return isMember || isStaff || this.currentUser?.admin;
  }

  @computed("model.member_group.can_see_members")
  get showMembers() {
    return this.model.member_group.can_see_members;
  }

  @computed("model.can_manage_staff", "model.staff_group.is_group_user")
  get showStaff() {
    return (
      this.currentUser &&
      (this.model.can_manage_staff || this.model.staff_group.is_group_user)
    );
  }

  @computed("model.can_manage_membership_requests")
  get showRequests() {
    return this.model.can_manage_membership_requests;
  }

  @computed("model.can_edit")
  get showManage() {
    return this.model.can_edit;
  }

  @action
  messageGroup() {
    this.send("createNewMessageViaParams", {
      recipients: this.get("model.name"),
      hasGroups: true,
    });
  }

  @action
  toggleDeleteTooltip() {
    // TODO is this doing anything?
    this.toggleProperty("showTooltip");
  }
}

class Tab {
  #name;
  #route;
  #icon;
  #i18nKey;
  #options;

  constructor(props) {
    const { name, route, icon, i18nKey, options } = props;

    this.#name = name;
    this.#route = route;
    this.#icon = icon;
    this.#i18nKey = i18nKey;
    this.#options = options;
  }

  get name() {
    return this.#name;
  }

  get route() {
    return this.#route || `circle.${this.#name}`;
  }

  get icon() {
    return this.#icon;
  }

  get title() {
    return i18n(this.#i18nKey || `discourse_circles.circle.${this.#name}`);
  }

  get options() {
    return this.#options || {};
  }
}
